import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ICONS from "../../../../assets/Icons";
import CategoriesEdit from "../../../../components/_utils/AccountCreationComponents/CategoriesEdit";
import SmallPopup from "../../../../components/_utils/CommonSmallPopup";
import PrimaryStepper from "../../../../components/_utils/CommonSteppers/PrimaryStepper";
import SVGComponent from "../../../../components/_utils/SVGComponent";
import {
  editBeneficiary,
  resetEditBeneficiary,
} from "../../../../redux/features/accounts/BeneficiaryAccount/createAccount";
import { viewAccountDetail } from "../../../../redux/features/verifyPAN/verifyPAN";
import { GlobalConfiguration } from "../../../../utils/configurationProvider";
import "./index.css";
import CommonLayout from "../../../../components/_utils/CommonLayout";

const BeneEditCategories = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = params;

  const [dataLoaded, setDataLoaded] = useState(false);
  const [openPopEdit, setOpenPopEdit] = useState(false);
  const [updateActive, setUpdateActive] = useState(false);
  const [sliceCategories, setSliceCategories] = useState([]);
  const [initialCategories, setInitialCategories] = useState([]);
  const [isUpdateDisabled, setIsUpdateDisabled] = useState(true);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showAlertCategories, setShowAlertCategories] = useState(false);

  const { viewAccountDetailData, viewAccountDetailLoading } = useSelector(
    (state) => state.verify,
  );

  const sliceCategory = useSelector(
    (state) => state.beneAllVerification.beneTransCategories.categories,
  );

  const { editBeneficiaryData } = useSelector(
    (state) => state.beneCreateAccount,
  );

  useEffect(() => {
    const fetchCategoryData = async () => {
      try {
        let response1 = await dispatch(viewAccountDetail({ id }));

        if (
          response1?.error?.message === "Request failed with status code 404"
        ) {
          navigate("*");
          return;
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchCategoryData();
  }, [dispatch, id, navigate]);

  useEffect(() => {
    setSliceCategories(
      sliceCategory.filter((category) => category?.active === "Y"),
    );
  }, [sliceCategory]);

  useEffect(() => {
    if (!viewAccountDetailLoading && viewAccountDetailData?.status === true) {
      const beneficiaryData = viewAccountDetailData?.beneficiary_accounts[0];

      const selectedCategoriesArray = beneficiaryData.category
        .split(",")
        .filter((val) => val !== "");

      setSelectedCategories(selectedCategoriesArray);
      setInitialCategories(selectedCategoriesArray);
      setDataLoaded(true);
    }
  }, [dispatch, viewAccountDetailData, viewAccountDetailLoading]);

  useEffect(() => {
    if (!dataLoaded || sliceCategories.length === 0) return;

    const allInitiallyAssigned =
      initialCategories.length === sliceCategories.length;

    const sameAsInitial =
      selectedCategories.length === initialCategories.length &&
      selectedCategories.every((cat) => initialCategories.includes(cat));

    if (initialCategories.length === 0 && selectedCategories.length === 0) {
      setIsUpdateDisabled(false);
    } else if (sameAsInitial) {
      setIsUpdateDisabled(true);
    } else if (allInitiallyAssigned && selectedCategories.length === 0) {
      setIsUpdateDisabled(false);
    } else {
      setIsUpdateDisabled(false);
    }

    setSelectAllChecked(
      sliceCategories.length > 0 &&
        selectedCategories.length === sliceCategories.length,
    );
  }, [selectedCategories, sliceCategories, initialCategories, dataLoaded]);

  useEffect(() => {
    if (editBeneficiaryData?.status === true) {
      navigate(
        `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/category_edit_success/${id}`,
        { state: { updatedFields: ["category"] }, replace: true },
      );

      dispatch(resetEditBeneficiary());
    }
  }, [editBeneficiaryData, dispatch, id, navigate]);

  const handleBack = () => {
    if (!isUpdateDisabled) {
      setOpenPopEdit(true);
    } else {
      navigate(
        `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${id}`,
      );
    }
  };

  const handleUpdateAccount1 = async () => {
    if (updateActive) return;

    const activeCategoriesCount = sliceCategory.filter(
      (category) => category?.active === "Y",
    )?.length;

    const willShowUnsafePopup =
      (selectedCategories?.length === activeCategoriesCount &&
        !showAlertCategories) ||
      (!selectedCategories?.length && !showAlertCategories);

    if (willShowUnsafePopup) {
      setShowAlertCategories(true);
    } else {
      setUpdateActive(true);

      try {
        await await dispatch(
          editBeneficiary({
            id: id,
            category: selectedCategories.toString(),
            navigate,
          }),
        ).unwrap();

        setShowAlertCategories(false);
      } catch (error) {
        console.error("Update failed:", error);
      } finally {
        setUpdateActive(false);
      }
    }
  };

  const toggleSelectAll = () => {
    if (selectedCategories.length === sliceCategories.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(sliceCategories.map((cat) => cat.name));
    }
  };

  const handleCategorySelect = (categoryId) => {
    const category = sliceCategories.find((c) => c.id === categoryId);
    if (!category) return;

    const updated = selectedCategories.includes(category.name)
      ? selectedCategories.filter((name) => name !== category.name)
      : [...selectedCategories, category.name];

    setSelectedCategories(updated);
  };

  const handleConfirm = () => {
    navigate(
      `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${id}`,
    );
  };

  const sortedCategories = sliceCategories;

  const dataArray = [
    {
      previousText: "Cancel",
      nextText: "Update",
      previousEnableBgColor: "var(--primary-nav-act)",
      previousDisableBgColor: "var(--primary-nav-act)",
      previousEnableTextColor: "var(--text-primary)",
      previousHoverOption: "white",
      nextHoverOption: "black",
      nextIsEnabled: !isUpdateDisabled,
      previousIsDisabled: updateActive,
      nextIsLoading: updateActive,
      nextLoaderNeeded: true,
      nextEnableBgColor: "var(--positive-act)",
      nextDisableBgColor: updateActive
        ? "var(--positive-act)"
        : "var(--text-disabled)",
      nextEnableTextColor: "var(--text-primary)",
      nextDisableTextColor: "var(--text-primary)",
      stepperText: "Category",
    },
  ];

  return (
    <div className="flex justify-center h-full-height bg-primary-bg">
      <div
        className="h-full w-full max-w-tab-width-normal"
        style={{
          overflowY: showAlertCategories ? "hidden" : "scroll",
          scrollbarWidth: "none",
        }}
      >
        {showAlertCategories && (
          <div className="fixed top-top-base left-left-base w-width-full h-height-full flex items-center bg-PopUp-BackDrop justify-center z-z-index-modal bg-opacity-dark-opacity">
            <SmallPopup
              headerTitle={"It is unsafe to proceed"}
              headerFontWeight="semibold"
              headerFontColor="negative-act"
              textSize="font-md"
              headerWithIcon={ICONS?.ALERT}
              subtext="Assigning all categories to a single account is highly unsafe.
                  It is recommended to limit the number of categories assigned
                  to the account."
              btnOneText="Cancel"
              btnOneBtnType="button"
              btnOneEnabledBgColor="transparent"
              btnOneHandleFunction={() => {
                setShowAlertCategories(false);
                setUpdateActive(false);
              }}
              btnOneOutLineColor="var(--text-primary)"
              btnOneEnabledTextColor="var(--text-primary)"
              btnOneIsDisabled={false}
              btnOneHoverOption="white"
              btnTwoText="Proceed"
              btnTwoBtnType="button"
              btnTwoHandleFunction={handleUpdateAccount1}
              btnTwoEnabledBgColor="var(--positive-act)"
              btnTwoEnabledTextColor="var(--text-primary)"
              btnTwoIsDisabled={false}
              btnTwoHoverOption="black"
            />
          </div>
        )}
        {openPopEdit && (
          <div className="fixed top-top-base left-left-base w-width-full h-height-full flex items-center bg-PopUp-BackDrop justify-center z-z-index-modal bg-opacity-dark-opacity">
            <SmallPopup
              headerTitle={"Are you sure you want to leave this page?"}
              headerFontWeight="semibold"
              headerFontColor="text-primary"
              textSize="font-md"
              subtext="Leaving this page will exit you from current flow."
              btnOneText="Cancel"
              btnOneBtnType="button"
              btnOneEnabledBgColor="transparent"
              btnOneHandleFunction={() => setOpenPopEdit(false)}
              btnOneOutLineColor="var(--text-primary)"
              btnOneEnabledTextColor="var(--text-primary)"
              btnOneIsDisabled={false}
              btnOneHoverOption="white"
              btnTwoText="Leave"
              btnTwoBtnType="button"
              btnTwoHandleFunction={handleConfirm}
              btnTwoEnabledBgColor="var(--text-primary)"
              btnTwoEnabledTextColor="var(--primary-bg)"
              btnTwoIsDisabled={false}
              btnTwoHoverOption="black"
            />
          </div>
        )}
        <div className="h-navbar-breadcrumb-height hidden Qmd:block"></div>
        <div className="main_edit_category_stepper_div  Qmd:relative ">
          <PrimaryStepper
            activeStep={GlobalConfiguration?.lengthCheckZero}
            handleForwardAction={handleUpdateAccount1}
            handleBackwardAction={handleBack}
            dataArray={dataArray}
          />
        </div>
        <div className="w-width-full relative edit-cate-top-bottom-div">
          <div className="bg-primary-nav fixed top-top-base w-width-full flex z-z-index-modal shadow-box-shadow-bottom">
            <div
              className="w-icon-md h-icon-md m-margin-md"
              onClick={() => handleBack()}
            >
              <SVGComponent
                url={ICONS?.ARROW_LEFT_WHITE}
                color="var(--text-primary)"
                width="var(--icon-sm)"
                height="var(--icon-sm)"
              />
            </div>
            <div className="py-padding-sm">
              <p className="text-text-primary text-font-md">
                Edit Beneficiary Account
              </p>
              <p className="text-text-secondary text-font-xs">
                Assign beneficiary categories
              </p>
            </div>
          </div>
          <div
            className={`h-mobile-create-header ${
              !isUpdateDisabled
                ? "bg-positive-act cursor-pointer"
                : "bg-text-disabled cursor-not-allowed"
            } fixed bottom-bottom-base w-width-full text-text-primary items-center justify-center flex font-semibold z-z-index-modal`}
            onClick={!isUpdateDisabled ? handleUpdateAccount1 : undefined}
          >
            Update
          </div>
        </div>
        <div className="dividecategory-edit-flow px-padding-md Qlg:px-padding-base">
          <div className="contactright mt-margin-md">
            <CommonLayout>
              <CategoriesEdit
                sortedCategories={sortedCategories}
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
                selectAllChecked={selectAllChecked}
                toggleSelectAll={toggleSelectAll}
                handleCategorySelect={handleCategorySelect}
                Title={"Assign Categories"}
                SubTitle="Not selecting categories will assign all, including future ones, to this account. This is not recommended for security reasons."
                TitleFontcolor="text-primary"
                TextSize="font-md"
                SubTitleFontcolor="text-secondary"
                SubTitleTextSize="font-xs"
                TitleFontWeight="semibold"
                SubTitleFontWeight="normal"
              />
            </CommonLayout>
            {/* <div className="flex flex-col">
              <div className=" mt-margin-md rounded-border-radius-md"></div>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BeneEditCategories;
